import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../services/axiosInstance";
import { API_ENDPOINTS } from "../../services/api/apiConfig";
import { ArrowLeft } from "lucide-react";
import Header from "../common/Header";
import LoadingScreen from "../common/LoadingScreen";
import ResponseMessage from "../common/ResponseMessage";
import type { MedicalRecord } from "../../types";

const RecordView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [record, setRecord] = useState<MedicalRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No token found");
        
        // Récupérer le dossier médical du patient
        const response = await axiosInstance.get(
          API_ENDPOINTS.MEDICAL_RECORDS.BY_PATIENT(Number(id))
        );
        
        // Si plusieurs dossiers, prendre le plus récent
        const records = response.data;
        const latestRecord = Array.isArray(records) ? records[0] : records;
        
        setRecord(latestRecord);
        setLoading(false);
      } catch (err) {
        setError("Erreur lors de la récupération du dossier médical");
        setLoading(false);
        console.error("Error fetching record:", err);
      }
    };

    if (id) {
      fetchRecord();
    }
  }, [id]);

  const renderFilePreview = (url: string | null) => {
    if (!url) return <p className="text-gray-400 italic">Aucun fichier</p>;
    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
    const isPdf = /\.pdf$/i.test(url);

    if (isImage) {
      return (
        <img
          src={url}
          alt="Aperçu fichier"
          className="max-w-full max-h-64 rounded-lg border"
        />
      );
    } else if (isPdf) {
      return (
        <iframe
          src={url}
          title="PDF Preview"
          className="w-full h-64 rounded-lg border"
        />
      );
    } else {
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline"
        >
          Télécharger le fichier
        </a>
      );
    }
  };

  if (loading)
    return (
      <LoadingScreen message="Chargement du dossier médical..." role="admin" />
    );

  if (error || !record)
    return (
      <ResponseMessage
        message={error || "Dossier médical non trouvé"}
        role="admin"
      />
    );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 py-8 px-4 sm:px-6 lg:px-8">
      <Header
        title="Dossier Médical"
        subtitle="Détails du dossier médical du patient."
        icon={<ArrowLeft className="w-6 h-6 text-white" />}
        role="admin"
        backLink="/admin/dashboard/record"
        description=""
      />

      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden animate-slideInUp">
          <div className="p-6 md:p-8 space-y-8">
            {/* Infos Patient + Médecin */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-indigo-700 border-b pb-2">
                  Informations Patient
                </h3>
                <p>
                  <strong>Nom:</strong> {record.patientFullName}
                </p>
                <p>
                  <strong>Téléphone:</strong> {record.phone}
                </p>
                <p>
                  <strong>Date de création:</strong>{" "}
                  {new Date(record.dateCreation).toLocaleDateString("fr-FR")}
                </p>
              </div>
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-indigo-700 border-b pb-2">
                  Médecin
                </h3>
                <p>
                  <strong>Nom du Médecin:</strong> {record.doctorName}
                </p>
              </div>
            </div>

            {/* Symptômes + Antécédents */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-indigo-700 border-b pb-2">
                  Symptômes
                </h3>
                <p>
                  <strong>Symptôme:</strong> {record.symptoms}
                </p>
                <p>
                  <strong>Durée des symptômes:</strong> {record.symptomDuration}
                </p>
                <p>
                  <strong>Médicaments actuels:</strong>{" "}
                  {record.currentMedication}
                </p>
              </div>
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-indigo-700 border-b pb-2">
                  Antécédents
                </h3>
                <p>
                  <strong>Conditions préexistantes:</strong>{" "}
                  {record.preexistingCondition}
                </p>
                <p>
                  <strong>Antécédents familiaux:</strong> {record.familyHistory}
                </p>
                <p>
                  <strong>Allergies:</strong> {record.allergies}
                </p>
              </div>
            </div>

            {/* Évaluation */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-indigo-700 border-b pb-2">
                Évaluation
              </h3>
              <p>
                <strong>Évaluation:</strong> {record.assessment}
              </p>
              <p>
                <strong>Recommandation:</strong> {record.recommendation}
              </p>
            </div>

            {/* Fichiers Médicaux */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-indigo-700 border-b pb-2">
                Fichiers Médicaux
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="font-medium mb-1">Prescription</p>
                  {renderFilePreview(record.prescriptionUrl ?? null)}
                </div>
                <div>
                  <p className="font-medium mb-1">Résultat</p>
                  {renderFilePreview(record.resultUrl ?? null)}
                </div>
              </div>
            </div>
          </div>

          {/* Bouton retour */}
          <div className="flex justify-end pt-6 border-t">
            <button
              onClick={() => navigate("/admin/dashboard/record")}
              className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg shadow-sm transition-colors"
              aria-label="Retour à la liste"
            >
              Retour à la liste
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecordView;
